import { useCallback, useEffect, useRef, useState } from "react";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  Item,
  Scrolling,
  SearchPanel,
  Selection,
  Toolbar,
} from "devextreme-react/data-grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { convertUTCTimeToLocal } from "../../util/Utils";

interface SiteSelectionPopupProps {
  isOpen: boolean;
  preSelectedIds: number[];
  appName: string | null;
  selectedTemplates?: string[];
  onClose: () => void;
  onConfirm: (selectedIds: number[]) => void;
}

type SiteRow = {
  hqSiteId?: number;
  name?: string;
  templateName?: string;
  mdmClientId?: number;
  clientName?: string;
  appName?: string;
  year?: number | string;
  consentStatus?: string;
  created?: Date | string;
  [key: string]: any;
};

export const SiteSelectionPopup = ({
  isOpen,
  preSelectedIds,
  appName,
  selectedTemplates,
  onClose,
  onConfirm,
}: SiteSelectionPopupProps) => {
  const dataGridRef = useRef<DataGrid>(null);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const loadSites = useCallback(async () => {
    setIsLoading(true);
    try {
      const appNames = appName ? appName.split(",").map((a) => a.trim()) : [null];
      const responses = await Promise.all(
        appNames.map((name) =>
          createSitesClientApi.highQ_GetSiteFromTrack("ACTIVE", name, 0, 10000)
        )
      );
      const templateNames = new Set((selectedTemplates ?? []).map((template) => template.trim().toLowerCase()));
      const rawData: SiteRow[] = responses.flatMap((r) => (r as any)?.data?.data || [])
        .filter((site: SiteRow) => templateNames.size === 0 || templateNames.has(site.templateName?.trim().toLowerCase() ?? ""));
      const visibleSiteIds = new Set(rawData.map((site) => site.hqSiteId));
      const selectedIds = templateNames.size > 0
        ? (preSelectedIds ?? []).filter((siteId) => visibleSiteIds.has(siteId))
        : preSelectedIds ?? [];
      const preSelected = new Set(selectedIds);
      rawData.sort((a, b) => {
        const aSelected = preSelected.has(a.hqSiteId!) ? 0 : 1;
        const bSelected = preSelected.has(b.hqSiteId!) ? 0 : 1;
        return aSelected - bSelected;
      });
      setSites(rawData);
      setSelectedRowKeys(selectedIds);
      setTimeout(() => {
        refreshGridSelection(selectedIds);
      }, 0);

    } catch {
      setSites([]);
    } finally {
      setIsLoading(false);
    }
  }, [appName, preSelectedIds, selectedTemplates]);


  useEffect(() => {
    if (isOpen) {
      setSelectedRowKeys(preSelectedIds ?? []);
      loadSites();
    }

    return () => {
      setSites([]);
      setSelectedRowKeys([]);
    }
  }, [isOpen, preSelectedIds, loadSites]);

  const handleConfirm = useCallback(() => {
    onConfirm(selectedRowKeys.filter((k) => k !== null && k !== undefined));
  }, [selectedRowKeys, onConfirm]);

  const cellRenderDate = (e: any) => {
    if (!e.data.created) return "";
    return <div>{convertUTCTimeToLocal(String(e.data.created))}</div>;
  };

  const handleClose = () => {
    setSites([]);
    onClose();
  }

  const refreshGridSelection = (selectedIds: number[]) => {
    const gridInstance = dataGridRef.current?.instance;

    if (!gridInstance) return;

    gridInstance.refresh().then(() => {
      gridInstance.option("selectedRowKeys", selectedIds);
      gridInstance.selectRows(selectedIds, false);
      gridInstance.repaint();
    });
  };


  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="site-selection-dialog-title"
      sx={{ zIndex: 1600 }}
    >
      <DialogTitle id="site-selection-dialog-title">
        Select Sites
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme: any) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <DataGrid
          keyExpr="hqSiteId"
          ref={dataGridRef}
          dataSource={sites}
          showBorders
          noDataText={isLoading ? "Loading..." : "No data"}
          selectedRowKeys={selectedRowKeys}
          onSelectionChanged={(e: any) => setSelectedRowKeys(e.selectedRowKeys)}
          height={500}
          allowColumnResizing
          columnResizingMode="widget"
          cacheEnabled={false}
        >
          <Selection
            mode="multiple"
            selectAllMode="page"
            showCheckBoxesMode="always"
            key="hqSiteId"
          />
          <FilterRow visible />
          <HeaderFilter visible />
          <SearchPanel visible placeholder="Search..." />
          <Scrolling columnRenderingMode="standard" />
          <Toolbar>
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <Column
            dataField="hqSiteId"
            caption="Site ID"
            width={90}
            alignment="left"
            allowHeaderFiltering={false}
          />
          <Column
            dataField="name"
            caption="Site Name"
            minWidth={200}
            alignment="left"
          />
          <Column
            dataField="templateName"
            caption="Site Template"
            minWidth={150}
            alignment="left"
          />
          <Column
            dataField="mdmClientId"
            caption="Master Client ID"
            width={130}
            alignment="left"
            allowHeaderFiltering={false}
          />
          <Column
            dataField="clientName"
            caption="Master Client Name"
            minWidth={180}
            alignment="left"
          />
          <Column
            dataField="appName"
            caption="Capability"
            width={100}
            alignment="left"
          />
          <Column
            dataField="year"
            caption="Year"
            width={80}
            alignment="left"
            allowHeaderFiltering={false}
          />
          <Column
            dataField="consentStatus"
            caption="7216 Consent"
            width={120}
            alignment="left"
          />
          <Column
            dataField="created"
            caption="Created"
            width={180}
            alignment="left"
            cellRender={cellRenderDate}
            allowHeaderFiltering={false}
          />
        </DataGrid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
